import { ExtendedFile } from '@consigli/hooks';
import { Language } from '@consigli/types';
import { FC, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { WizardAnalyzeButton } from '@/features/package-creation/components/wizard-analyze-button';

import { PackageConfigurationForm } from './package-configuration-form';
import { ReportFileUploads } from './report-file-uploads';
import { PackageFormData } from './types';

interface ReportCreationFormProps {
  templateFiles: ExtendedFile[];
  setTemplateFiles: React.Dispatch<React.SetStateAction<ExtendedFile[]>>;
  documentFiles: ExtendedFile[];
  setDocumentFiles: React.Dispatch<React.SetStateAction<ExtendedFile[]>>;
  onAnalyze: (
    packageName: string,
    language: Language,
    reportType: string,
    location: string,
  ) => void;
  validateName: (value: string) => boolean | string;
}

export const ReportCreationForm: FC<ReportCreationFormProps> = ({
  templateFiles,
  setTemplateFiles,
  documentFiles,
  setDocumentFiles,
  onAnalyze,
  validateName,
}) => {
  const { t } = useTranslation();
  const { getValues, formState } = useFormContext<PackageFormData>();

  const handleAnalyzeClick = useCallback(() => {
    const values = getValues();

    const validationResult = validateName(values.packageName);
    if (validationResult !== true) {
      const errorMessage =
        typeof validationResult === 'string'
          ? validationResult
          : t('package-creation.package-name-required', 'Package name is required');
      toast.error(errorMessage);
      return;
    }

    if (!values.reportType || values.reportType.trim() === '') {
      toast.error(t('package-creation.reports.report-type-required', 'Report type is required'));
      return;
    }

    if (!values.location || values.location.trim() === '') {
      toast.error(t('package-creation.reports.location-required', 'Location is required'));
      return;
    }

    if (templateFiles.length === 0) {
      toast.error(
        t('package-creation.reports.sample-report-required', 'Please upload a sample report'),
      );
      return;
    }

    if (documentFiles.length === 0) {
      toast.error(
        t(
          'package-creation.reports.document-files-required',
          'Please upload at least one document',
        ),
      );
      return;
    }

    onAnalyze(values.packageName, values.language, values.reportType, values.location);
  }, [getValues, templateFiles.length, documentFiles.length, onAnalyze, t, validateName]);

  return (
    <div className="space-y-6">
      <PackageConfigurationForm validateName={validateName} />

      <ReportFileUploads
        templateFiles={templateFiles}
        setTemplateFiles={setTemplateFiles}
        documentFiles={documentFiles}
        setDocumentFiles={setDocumentFiles}
      />

      <div className="fc-w-full mt-6">
        <WizardAnalyzeButton
          localFiles={[...templateFiles, ...documentFiles]}
          onAnalyze={handleAnalyzeClick}
          disabled={formState.isValid === false}
        />
      </div>
    </div>
  );
};
